import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Upload, Search, Filter, Grid, List, Image, Play, Eye, 
  Copy, Trash2, Download, MoreVertical, X, CheckCircle,
  AlertCircle, Clock, FolderOpen
} from 'lucide-react';
import { mediaService } from '../services/mediaService';

const MediaManager = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('uploadDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewMedia, setPreviewMedia] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Charger les médias au démarrage
  useEffect(() => {
    loadMedia();
  }, []);

  // Appliquer les filtres et la recherche
  useEffect(() => {
    let filtered = [...mediaFiles];

    // Recherche
    if (searchQuery) {
      filtered = mediaService.searchMedia(filtered, searchQuery);
    }

    // Filtres
    filtered = mediaService.filterByCategory(filtered, selectedCategory);
    filtered = mediaService.filterByType(filtered, selectedType);

    // Tri
    filtered = mediaService.sortMedia(filtered, sortBy, sortOrder);

    setFilteredMedia(filtered);
  }, [mediaFiles, searchQuery, selectedCategory, selectedType, sortBy, sortOrder]);

  // Charger les médias
  const loadMedia = async () => {
    setIsLoading(true);
    try {
      const media = await mediaService.getAvailableMedia();
      setMediaFiles(media);
    } catch (error) {
      console.error('Erreur lors du chargement des médias:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Gestion du drag & drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, []);

  // Gestion de l'upload de fichiers
  const handleFileUpload = async (files) => {
    for (const file of files) {
      const fileId = `upload-${Date.now()}-${file.name}`;
      
      try {
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        
        const result = await mediaService.simulateUpload(file);
        
        if (result.success) {
          setMediaFiles(prev => [result.media, ...prev]);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }
      } catch (error) {
        console.error('Erreur upload:', error);
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }
    }
  };

  // Sélection de fichiers
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAllFiles = () => {
    setSelectedFiles(filteredMedia.map(media => media.id));
  };

  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Actions sur les médias
  const deleteMedia = (mediaId) => {
    setMediaFiles(prev => prev.filter(media => media.id !== mediaId));
    setSelectedFiles(prev => prev.filter(id => id !== mediaId));
  };

  const deleteSelectedMedia = () => {
    setMediaFiles(prev => prev.filter(media => !selectedFiles.includes(media.id)));
    clearSelection();
  };

  const downloadMedia = (media) => {
    const link = document.createElement('a');
    link.href = mediaService.getMediaUrl(media.path);
    link.download = media.name;
    link.click();
  };

  // Obtenir les catégories disponibles
  const categories = mediaService.getCategories(mediaFiles);
  const stats = mediaService.getMediaStats(filteredMedia);

  // Composant de carte média (vue grille)
  const MediaCard = ({ media }) => (
    <div 
      className={`bg-white rounded-lg shadow border overflow-hidden group cursor-pointer transition-all duration-200 ${
        selectedFiles.includes(media.id) ? 'ring-2 ring-blue-500' : 'hover:shadow-lg'
      }`}
      onClick={() => toggleFileSelection(media.id)}
    >
      <div className="aspect-square bg-gray-100 relative">
        {media.type === 'image' ? (
          <img
            src={mediaService.getMediaUrl(media.path)}
            alt={media.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-200 to-blue-400 flex items-center justify-center">
            <Play className="h-12 w-12 text-blue-600" />
          </div>
        )}
        
        {/* Overlay avec actions */}
        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPreviewMedia(media);
            }}
            className="p-2 bg-white rounded-full hover:bg-gray-100"
            title="Prévisualiser"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadMedia(media);
            }}
            className="p-2 bg-white rounded-full hover:bg-gray-100"
            title="Télécharger"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteMedia(media.id);
            }}
            className="p-2 bg-white rounded-full hover:bg-gray-100 text-red-600"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* Indicateur de sélection */}
        {selectedFiles.includes(media.id) && (
          <div className="absolute top-2 right-2">
            <CheckCircle className="h-6 w-6 text-blue-500 bg-white rounded-full" />
          </div>
        )}
      </div>
      
      <div className="p-3">
        <h4 className="font-medium text-sm truncate mb-1">{media.name}</h4>
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>{media.size}</span>
          <span className="capitalize">{media.category}</span>
        </div>
        {media.dimensions && (
          <div className="text-xs text-gray-400 mt-1">{media.dimensions}</div>
        )}
      </div>
    </div>
  );

  // Composant de ligne média (vue liste)
  const MediaRow = ({ media }) => (
    <tr 
      className={`hover:bg-gray-50 cursor-pointer ${
        selectedFiles.includes(media.id) ? 'bg-blue-50' : ''
      }`}
      onClick={() => toggleFileSelection(media.id)}
    >
      <td className="px-4 py-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden">
            {media.type === 'image' ? (
              <img
                src={mediaService.getMediaUrl(media.path)}
                alt={media.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-blue-200 flex items-center justify-center">
                <Play className="h-4 w-4 text-blue-600" />
              </div>
            )}
          </div>
          <div>
            <div className="font-medium text-sm">{media.name}</div>
            <div className="text-xs text-gray-500 capitalize">{media.type}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm">{media.size}</td>
      <td className="px-4 py-3 text-sm capitalize">{media.category}</td>
      <td className="px-4 py-3 text-sm">{media.dimensions || media.duration || '-'}</td>
      <td className="px-4 py-3 text-sm">
        {new Date(media.uploadDate).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPreviewMedia(media);
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title="Prévisualiser"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadMedia(media);
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title="Télécharger"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteMedia(media.id);
            }}
            className="p-1 hover:bg-gray-200 rounded text-red-600"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold">Gestionnaire de Médias</h1>
            <p className="text-gray-600">
              {stats.totalFiles} fichiers • {stats.totalSize} • {stats.imageCount} images • {stats.videoCount} vidéos
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedFiles.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedFiles.length} sélectionné{selectedFiles.length > 1 ? 's' : ''}
                </span>
                <button
                  onClick={deleteSelectedMedia}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                >
                  Supprimer
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                >
                  Désélectionner
                </button>
              </div>
            )}
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Upload className="h-4 w-4" />
              <span>Ajouter des médias</span>
            </button>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Recherche */}
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher des médias..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtres */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous types</option>
            <option value="image">Images</option>
            <option value="video">Vidéos</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="uploadDate-desc">Plus récent</option>
            <option value="uploadDate-asc">Plus ancien</option>
            <option value="name-asc">Nom A-Z</option>
            <option value="name-desc">Nom Z-A</option>
            <option value="size-desc">Taille décroissante</option>
            <option value="size-asc">Taille croissante</option>
          </select>

          {/* Mode d'affichage */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              title="Vue grille"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              title="Vue liste"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Zone de drop principale */}
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex-1 relative ${isDragOver ? 'bg-blue-50' : ''}`}
      >
        {/* Overlay de drop */}
        {isDragOver && (
          <div className="absolute inset-0 bg-blue-100 bg-opacity-75 border-2 border-dashed border-blue-400 flex items-center justify-center z-10">
            <div className="text-center">
              <Upload className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <p className="text-xl font-semibold text-blue-700">Déposez vos fichiers ici</p>
              <p className="text-blue-600">Formats supportés: JPG, PNG, MP4, GIF</p>
            </div>
          </div>
        )}

        {/* Contenu principal */}
        <div className="h-full overflow-auto p-6">
          {/* Zone d'upload vide */}
          {filteredMedia.length === 0 && !isLoading && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  {searchQuery || selectedCategory !== 'all' || selectedType !== 'all' 
                    ? 'Aucun média trouvé'
                    : 'Aucun média disponible'
                  }
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery || selectedCategory !== 'all' || selectedType !== 'all'
                    ? 'Essayez de modifier vos critères de recherche'
                    : 'Glissez-déposez vos fichiers ici ou cliquez sur "Ajouter des médias"'
                  }
                </p>
                {!searchQuery && selectedCategory === 'all' && selectedType === 'all' && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 mx-auto"
                  >
                    <Upload className="h-5 w-5" />
                    <span>Ajouter des médias</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Vue grille */}
          {viewMode === 'grid' && filteredMedia.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {filteredMedia.map(media => (
                <MediaCard key={media.id} media={media} />
              ))}
            </div>
          )}

          {/* Vue liste */}
          {viewMode === 'list' && filteredMedia.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedFiles.length === filteredMedia.length && filteredMedia.length > 0}
                          onChange={selectedFiles.length === filteredMedia.length ? clearSelection : selectAllFiles}
                        />
                        <span>Nom</span>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Détails</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMedia.map(media => (
                    <MediaRow key={media.id} media={media} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Indicateurs de progression d'upload */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="fixed bottom-4 right-4 space-y-2">
              {Object.entries(uploadProgress).map(([fileId, progress]) => (
                <div key={fileId} className="bg-white rounded-lg shadow-lg p-4 w-80">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-blue-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Upload en cours...</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de prévisualisation */}
      {previewMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">{previewMedia.name}</h3>
                <p className="text-sm text-gray-600">
                  {previewMedia.size} • {previewMedia.dimensions || previewMedia.duration}
                </p>
              </div>
              <button
                onClick={() => setPreviewMedia(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              {previewMedia.type === 'image' ? (
                <img
                  src={mediaService.getMediaUrl(previewMedia.path)}
                  alt={previewMedia.name}
                  className="max-w-full max-h-[60vh] object-contain mx-auto"
                />
              ) : (
                <video
                  src={mediaService.getMediaUrl(previewMedia.path)}
                  controls
                  className="max-w-full max-h-[60vh] mx-auto"
                >
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <div>Catégorie: {previewMedia.category}</div>
                  <div>Upload: {new Date(previewMedia.uploadDate).toLocaleString()}</div>
                  <div>Tags: {previewMedia.tags?.join(', ')}</div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadMedia(previewMedia)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Télécharger</span>
                  </button>
                  <button
                    onClick={() => {
                      deleteMedia(previewMedia.id);
                      setPreviewMedia(null);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input file caché */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => handleFileUpload(Array.from(e.target.files))}
        className="hidden"
      />
    </div>
  );
};

export default MediaManager;